/**
 * 通知记录详情 Drawer
 * 根据 notification/frontend-spec.md 规范定义
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Descriptions, Button, Space, Tag, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { NotificationRecordDTO } from '@/types/notification/notificationRecord';
import {
  NOTIFICATION_TYPE_OPTIONS,
  NOTIFICATION_STATUS_OPTIONS,
  NOTIFICATION_CHANNEL_OPTIONS,
  NOTIFICATION_PRIORITY_OPTIONS,
  NotificationStatus,
} from '@/constants/notification';
import { resendNotifications } from '@/services/notification/notificationService';

interface Props {
  visible: boolean;
  record: NotificationRecordDTO | null;
  onClose: () => void;
  onRefresh: () => void;
}

const NotificationRecordDetail: React.FC<Props> = ({
  visible,
  record,
  onClose,
  onRefresh,
}) => {
  const { t } = useTranslation();

  if (!record) return null;

  // 渲染标签
  const renderTag = (value: string, options: { value: string; label: string; color: string }[]) => {
    const option = options.find(opt => opt.value === value);
    return option ? <Tag color={option.color}>{t(option.label)}</Tag> : value;
  };

  // 渲染时间
  const renderTime = (time: number | null) => {
    return time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  // 重发
  const handleResend = async () => {
    try {
      const response = await resendNotifications({
        notificationIds: [record.notificationId],
      });
      if (response.success) {
        message.success(t('notification.record.list.message.resendSuccess'));
        onRefresh();
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      message.error(t('common.message.operationFailed'));
    }
  };

  return (
    <Drawer
      title={t('notification.record.detail.title')}
      placement="right"
      width={720}
      open={visible}
      onClose={onClose}
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            {record.status === NotificationStatus.FAILED && (
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleResend}
              >
                {t('notification.record.detail.actions.resend')}
              </Button>
            )}
            <Button onClick={onClose}>
              {t('common.close')}
            </Button>
          </Space>
        </div>
      }
    >
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label={t('notification.record.detail.notificationId')}>
          {record.notificationId}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.notificationType')}>
          {renderTag(record.notificationType, NOTIFICATION_TYPE_OPTIONS)}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.channel')}>
          {renderTag(record.channel, NOTIFICATION_CHANNEL_OPTIONS)}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.priority')}>
          {renderTag(record.priority, NOTIFICATION_PRIORITY_OPTIONS)}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.status')}>
          {renderTag(record.status, NOTIFICATION_STATUS_OPTIONS)}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.title')}>
          {record.title}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.content')}>
          {record.content}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.targetType')}>
          {record.targetType}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.targetId')}>
          {record.targetId}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.targetName')}>
          {record.targetName}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.relatedOrderNo')}>
          {record.relatedOrderNo || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.storeCode')}>
          {record.storeCode}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.storeName')}>
          {record.storeName}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.scheduledTime')}>
          {renderTime(record.scheduledTime)}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.sentTime')}>
          {renderTime(record.sentTime)}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.deliveredTime')}>
          {renderTime(record.deliveredTime)}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.readTime')}>
          {renderTime(record.readTime)}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.failReason')}>
          {record.failReason ? (
            <span style={{ color: 'red' }}>{record.failReason}</span>
          ) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.retryCount')}>
          {record.retryCount}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.createTime')}>
          {dayjs(record.gmtCreate).format('YYYY-MM-DD HH:mm:ss')}
        </Descriptions.Item>
        <Descriptions.Item label={t('notification.record.detail.creatorName')}>
          {record.creatorName}
        </Descriptions.Item>
      </Descriptions>
    </Drawer>
  );
};

export default NotificationRecordDetail;
